import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerIntl, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CustomInputComponent } from './components/custom-input/custom-input.component';
import { FormErrorPipe } from './pipes/form-error.pipe';
import { ControlRequiredPipe } from './pipes/control-required.pipe';
import { NumbersDirective } from './directives/numbers/numbers.directive';
import { DaysUntilDateComponent } from './components/days-until-date/days-until-date.component';
import { ReadonlyInputComponent } from './components/readonly-input/readonly-input.component';
import { NullDefaultValueDirective } from './directives/null-default-value/null-default-value.directive';
import { FieldHolderComponent, FieldHolderContentDirective } from './components/field-holder/field-holder.component';
import { UnsavedDataDirective } from './directives/unsaved-data/unsaved-data.directive';
import { RemoveOnPasteDirective } from './directives/remove-on-paste/remove-on-paste.directive';
import { RefreshOnChangeDirective } from './directives/refresh-on-change/refresh-on-change.directive';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { UnsavedDataGuard } from './guards';
import { TooltipModule } from '../tooltip/tooltip.module';
import { CustomDatepickerIntlService } from './services/custom-datepicker-intl.service';
import { CapitalizeDirective } from './directives/capitalize/capitalize.directive';
import { MatMomentDatetimeModule } from '@mat-datetimepicker/moment';
import { MAT_DATETIME_FORMATS } from '@mat-datetimepicker/core';
import { TouchUiAttrInjectorDirective } from './directives/touch-ui-attr-injector/touch-ui-attr-injector.directive';
import { TrimValueDirective } from './directives/trim-value/trim-value.directive';
import { CustomMomentDateAdapter } from './services/custom-moment-date.adapter';
import { FormNativeElementInjectorDirective } from './directives/form-native-element-injector/form-native-element-injector.directive';
import { FindInvalidSelectPlaceholderDirective } from './directives/select-a11y-fix/select-a11y-fix.directive';
import { MatSelectSearchPatchDirective } from './directives/mat-select-search-patch/mat-select-search-patch.directive';
import { PostalZoneInputComponent } from './components/postal-zone-input/postal-zone-input.component';
import { NgxMaskModule, NgxMaskPipe } from 'ngx-mask';
import {TranslateModule} from '@ngx-translate/core';
import {SharedModule} from '@shared/shared.module';

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const CUSTOM_DATETIME_FORMATS = {
  parse: {
    dateInput: CUSTOM_DATE_FORMATS.parse.dateInput,
    monthInput: 'MMMM',
    timeInput: 'LT',
    datetimeInput: `${CUSTOM_DATE_FORMATS.parse.dateInput} LT`
  },
  display: {
    dateInput: CUSTOM_DATE_FORMATS.display.dateInput,
    monthInput: 'MMMM',
    datetimeInput: `${CUSTOM_DATE_FORMATS.parse.dateInput} LT`,
    timeInput: 'LT',
    monthYearLabel: CUSTOM_DATE_FORMATS.display.monthYearLabel,
    dateA11yLabel: CUSTOM_DATE_FORMATS.display.dateA11yLabel,
    monthYearA11yLabel: CUSTOM_DATE_FORMATS.display.monthYearA11yLabel,
    popupHeaderDateLabel: 'ddd, DD MMM'
  }
};

const materialModules = [
  MatFormFieldModule,
  MatInputModule,
  MatMomentDateModule,
  MatIconModule,
  MatDatepickerModule,

  MatMomentDatetimeModule,
];

@NgModule({
  imports: [
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    NgxMaskModule.forChild(),
    TranslateModule.forChild(),

    materialModules
  ],
  declarations: [
    CustomInputComponent,
    ControlRequiredPipe,
    FormErrorPipe,
    NumbersDirective,
    DaysUntilDateComponent,
    ReadonlyInputComponent,
    NullDefaultValueDirective,
    FieldHolderComponent,
    FieldHolderContentDirective,
    UnsavedDataDirective,
    RemoveOnPasteDirective,
    FormNativeElementInjectorDirective,
    TouchUiAttrInjectorDirective,
    RefreshOnChangeDirective,
    CapitalizeDirective,
    TrimValueDirective,
    FindInvalidSelectPlaceholderDirective,
    MatSelectSearchPatchDirective,
    PostalZoneInputComponent,
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    materialModules,

    CustomInputComponent,
    ControlRequiredPipe,
    FormErrorPipe,
    NumbersDirective,
    DaysUntilDateComponent,
    ReadonlyInputComponent,
    NullDefaultValueDirective,
    FieldHolderComponent,
    FieldHolderContentDirective,
    UnsavedDataDirective,
    RemoveOnPasteDirective,
    FormNativeElementInjectorDirective,
    TouchUiAttrInjectorDirective,
    RefreshOnChangeDirective,
    CapitalizeDirective,
    TrimValueDirective,
    FindInvalidSelectPlaceholderDirective,
    MatSelectSearchPatchDirective,
    PostalZoneInputComponent
  ],
  providers: [
    NgxMaskPipe,
    UnsavedDataGuard,
    { provide: MatDatepickerIntl, useClass: CustomDatepickerIntlService },
    { provide: DateAdapter, useClass: CustomMomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATETIME_FORMATS, useValue: CUSTOM_DATETIME_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { float: 'always' } }
  ]
})
export class CustomFormsModule {
}
